import React, {createContext, useContext} from 'react'
import PropTypes from 'prop-types'

const GDIDynamicContentContext = createContext({})

/**
 * Provides the dynamic content values consumed by GDI CMS blocks (notably
 * `gdiTextBlock`) when expanding inline `{tokenName}` tags inside authored
 * HTML. Hosts wrap a subtree of CMS content with this provider and pass a
 * `value` object whose keys correspond to the tokens that should resolve.
 *
 * Recognised keys (all optional — tokens with no value are left untouched
 * in the authored text):
 *   - pdpLoyaltyPoints, loyaltyCustomerFirstName,
 *     loyaltyMissingQualifyingPoints, loyaltyNextTier, loyaltyDueDate
 *     → simple string substitutions.
 *   - searchTerm: string — the active search query; use `{searchTerm}` in
 *     any GDITextBlock on the search-no-results page to show the user's
 *     search keyword (e.g. "No results for {searchTerm}").
 *   - closeParentModal: () => void
 *     → optional callback invoked by interactive tokens (e.g. the
 *       `{newsletterForm}` Subscribe button) after a successful action when
 *       the CMS content is embedded inside a modal/drawer. Hosts that
 *       render the provider inside a drawer should pass their `onClose`
 *       here so the drawer closes after submit. Non-modal hosts leave it
 *       undefined and the token simply skips the close step.
 *   - dynamicProductReference: string — SKU (or comma-separated SKU list) used
 *     as the reference for a nested `gdiCarouselGridBlock` whose dynamic
 *     content is a product-scoped Vicky model (Bought Together / Shop the
 *     Look / Similars) and that has no authored
 *     `ProductCarouselGridDynamicContentReference`. Hosts that aren't a PDP
 *     but still have a product context supply it here — e.g. the cart page
 *     passes the SKUs of every line item so a "Vicky - Bought Together"
 *     carousel authored on `GDICartPage` resolves against the basket. Takes
 *     lower priority than an authored reference and than `pdpProductSku`.
 *   - disableViewportBleed: boolean — when true, a nested
 *     `gdiCarouselGridBlock` suppresses its left/right viewport bleed so the
 *     scroll track stays within the wrapper (e.g. on desktop where the block
 *     sits next to sibling grid columns).
 *   - carouselPeekInset: boolean — when true, a nested
 *     `gdiCarouselGridBlock` that is a non-fluid partial-tile Carousel with
 *     its viewport bleed suppressed (via `disableViewportBleed`) still applies
 *     a peek gutter on its inner scroll track. The gutter width per side is
 *     taken from the authored `PaddingPerDevice`/`MarginPerDevice` left/right
 *     values (padding wins over margin; an unauthored side gets `0`, i.e. no
 *     gutter — nothing is hardcoded), and those horizontal values are stripped
 *     from the section box so they aren't applied twice. Use it alongside
 *     `disableViewportBleed` when the carousel sits inside a constrained
 *     container/drawer so tile 1 peeks in from a gutter instead of sitting
 *     flush against the container edge.
 *   - forceCarouselBleed: boolean — when true, a nested
 *     `gdiCarouselGridBlock` renders with the fluid-container bleed at every
 *     breakpoint regardless of the authored SectionWidth (full-bleed /
 *     max-width are suppressed). This reproduces the mobile carousel
 *     behaviour on desktop: tile 1 snaps at the page gutter (`FLUID_INSET`),
 *     slides scroll edge-to-edge over the gutters, and the last tile shows a
 *     trailing gutter — i.e. "start gutter → bleed → end gutter".
 */
export const GDIDynamicContentProvider = ({children, value}) => {
    return (
        <GDIDynamicContentContext.Provider value={value || {}}>
            {children}
        </GDIDynamicContentContext.Provider>
    )
}

export const useGDIDynamicContentContext = () => useContext(GDIDynamicContentContext)

GDIDynamicContentProvider.propTypes = {
    children: PropTypes.node,
    value: PropTypes.object
}
